/**
 * One place that decides what a user turn is about.
 *
 * Rules:
 * - Decide by what the user wants MADE, not by which words appear. In
 *   "make a quiz out of this presentation" the deliverable is a quiz; the
 *   presentation is only the source.
 * - Follow-ups ("create a new one", "show it to me", "no, use the Microlab
 *   bank") belong to the lane of the earlier turns.
 * - "Show me that quiz" is a lookup of real data, not a feature guide.
 *
 * The same rules run in mic-backend/src/services/mcpQuiz/assistantIntent.js.
 * Keep both files in sync (the tests share their cases).
 */

import { PRESENTATION_WORD, QUIZ_WORD } from "./quiz/quizDraftDefaults";

export const LANES = {
  QUIZ: "quiz",
  QUIZ_LOOKUP: "quiz_lookup",
  PRESENTATION: "presentation",
  // A question or request about an uploaded file (summarize, explain slide 5, …).
  FILE: "file",
  RESULTS: "results",
  USERS: "users",
  GUIDE: "guide",
  CLARIFY: "clarify",
  CHAT: "chat",
};

/** Lanes whose answer must come from Microlab tools and data, never from free text. */
export const DATA_LANES = new Set([
  LANES.QUIZ,
  LANES.QUIZ_LOOKUP,
  LANES.PRESENTATION,
  LANES.FILE,
  LANES.RESULTS,
  LANES.USERS,
]);

const CATALOGUE_TYPOS = [
  [/\b(histology|histolgy|histolog|histologi|histo|hystology|histalogy)\b/i, "histology"],
  [/\b(embryology|embryolgy|embriology|embryologi|embryo)\b/i, "embryology"],
  [/\b(anatomy|anatomi|anatmy|anotomy|antomy)\b/i, "anatomy"],
];

export function normalizeAskText(text) {
  return String(text || "")
    .replace(/\b(maka|mak|mae)\b/gi, "make")
    .replace(/\b(creat|craete|crate|cretae)\b/gi, "create")
    .replace(/\b(abotu|bout|abut)\b/gi, "about")
    .replace(/\b(oin|ont)\b/gi, "on")
    .replace(/\b(tusse|tisse|tissu|tisuue|tisssue|tisseu|tisue)\b/gi, "tissue")
    .replace(/\b(conective|conenctive|connetive)\b/gi, "connective")
    .replace(/\b(quizz|quize|qiuz|quizzz)\b/gi, "quiz")
    .replace(/\b(quizes|quizess|quizs)\b/gi, "quizzes")
    .replace(/\b(questons?|qustions?|quesitons?|questoins?)\b/gi, (m) =>
      /s$/i.test(m) ? "questions" : "question"
    )
    .replace(/\s+/g, " ")
    .trim();
}

function editDistance(a, b) {
  const s = String(a || "");
  const t = String(b || "");
  if (s === t) return 0;
  if (!s.length) return t.length;
  if (!t.length) return s.length;
  const prev = new Array(t.length + 1);
  const cur = new Array(t.length + 1);
  for (let j = 0; j <= t.length; j += 1) prev[j] = j;
  for (let i = 1; i <= s.length; i += 1) {
    cur[0] = i;
    for (let j = 1; j <= t.length; j += 1) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= t.length; j += 1) prev[j] = cur[j];
  }
  return prev[t.length];
}

/** “presentation” or a close typo (presnetion, oresenation, presntation, …). */
export function looksLikePresentationWord(token) {
  const w = String(token || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
  if (!w) return false;
  if (/^presenta?t?ions?$/.test(w)) return true;
  if (w.length < 8 || w.length > 16) return false;
  if (!/^(pres|pre|prs|ore|per|pra)/.test(w) || !/t?ions?$/.test(w)) return false;
  return (
    editDistance(w, "presentation") <= 3 ||
    editDistance(w.replace(/s$/, ""), "presentation") <= 3
  );
}

export function hasPresentationWord(text) {
  const raw = String(text || "");
  if (PRESENTATION_WORD.test(raw)) return true;
  return raw
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .some(looksLikePresentationWord);
}

const SOURCE_NOUN =
  /^(slides?|files?|decks?|pdfs?|pptx?|powerpoints?|lectures?|documents?|docs?|uploads?|attachments?|notes|chapters?|handouts?)$/i;

/**
 * Remove “from / out of / based on / using / on … this presentation|file|slides”
 * so a source never counts as the thing to make.
 */
export function stripSourcePhrases(text) {
  // Chained sources (“the slides of this presentation”) need more than one pass.
  let out = String(text || "");
  for (let pass = 0; pass < 3; pass += 1) {
    const next = stripSourcePhrasesOnce(out);
    if (next === out) break;
    out = next;
  }
  return out;
}

function stripSourcePhrasesOnce(text) {
  const isSourceWord = (w) => SOURCE_NOUN.test(w) || looksLikePresentationWord(w);
  return stripSourceRuns(
    String(text || "")
      // “no presentation”, “not a quiz”, “without slides”: a thing NOT to make.
      .replace(
        /\b(?:no|not|without|instead of|never|don'?t (?:make|create|want|need)|do not (?:make|create|want|need))\s+(?:a\s+|an\s+|any\s+|the\s+)?([^\s.,!?;:]+)/gi,
        (match, noun) =>
          isSourceWord(noun) || /^(quiz(zes)?|questions?|exams?|tests?|mcqs?)$/i.test(noun) ? " " : match
      )
      // “slide 5”, “pages 2–4” point at part of a source; “5 slides” is still a deck.
      .replace(/\b(slides?|pages?)\s*#?\s*\d{1,3}(?:\s*(?:-|–|to|and|,)\s*\d{1,3})*\b/gi, " ")
  );
}

const SOURCE_PREP = /^(from|using|off|of|in|on|with)$/i;
const SOURCE_DETERMINER = /^(the|this|that|these|those|my|our|your|a|an|uploaded|attached|above|same)$/i;

/**
 * “from this powerpoint presentation”, “of these lecture slides”: drop the
 * preposition and the whole run of source words. Scans word by word so an
 * earlier “with 20 questions” can't hide a later source phrase.
 */
function stripSourceRuns(text) {
  const isSourceWord = (w) => SOURCE_NOUN.test(w) || looksLikePresentationWord(w);
  const core = (token) => String(token || "").replace(/[^\w'-]/g, "");
  const tokens = String(text || "").split(/\s+/).filter(Boolean);
  const out = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const w = core(tokens[i]).toLowerCase();
    const next = core(tokens[i + 1]).toLowerCase();
    let start = -1;
    if (SOURCE_PREP.test(w)) start = i + 1;
    else if ((w === "out" && next === "of") || (w === "based" && next === "on")) start = i + 2;
    if (start > 0) {
      let k = start;
      while (k < tokens.length && SOURCE_DETERMINER.test(core(tokens[k]))) k += 1;
      if (k < tokens.length && isSourceWord(core(tokens[k]))) {
        while (k < tokens.length && isSourceWord(core(tokens[k]))) {
          const endsClause = /[.,!?;:]$/.test(tokens[k]);
          k += 1;
          if (endsClause) break;
        }
        i = k - 1;
        continue;
      }
    }
    out.push(tokens[i]);
  }
  return out.join(" ");
}


const CREATE_VERB =
  /\b(make|create|build|generate|prepare|draft|design|write|set\s*up|put together|give me|turn|convert|need|want|new)\b/i;

export function hasCreateVerb(text) {
  return CREATE_VERB.test(normalizeAskText(text));
}

/** Words that mean “a quiz” as the thing to produce. */
function mentionsQuizDeliverable(text) {
  const t = normalizeAskText(text);
  if (QUIZ_WORD.test(t)) return true;
  if (/\b(mcqs?|multiple[- ]choice|exam|test)\b/i.test(t) && CREATE_VERB.test(t)) return true;
  if (/\b(question bank|bank questions|microlab bank|public bank|private bank)\b/i.test(t)) return true;
  // “make 10 random questions”, “give me questions on…”, “20 hard questions”
  if (/\bquestions?\b/i.test(t)) {
    if (CREATE_VERB.test(t)) return true;
    if (/\b\d+\s+(?:\w+\s+){0,2}questions?\b/i.test(t)) return true;
  }
  return false;
}

/**
 * What does the user want produced?
 * Returns "quiz" | "presentation" | "both" | null.
 */
export function detectDeliverable(text) {
  const stripped = stripSourcePhrases(normalizeAskText(text));
  const quiz = mentionsQuizDeliverable(stripped);
  const presentation = hasPresentationWord(stripped);
  if (quiz && presentation) return "both";
  if (quiz) return "quiz";
  if (presentation) return "presentation";
  return null;
}

/** True when the message refers to a source document (file / slides / presentation). */
export function mentionsSource(text) {
  const raw = normalizeAskText(text);
  return stripSourcePhrases(raw) !== raw;
}

/** Every Microlab subject named, in order (“histology and anatomy”). */
export function parseCatalogues(text) {
  const raw = String(text || "");
  const found = [];
  for (const [pattern, name] of CATALOGUE_TYPOS) {
    const m = raw.match(new RegExp(pattern.source, "i"));
    if (m) found.push({ name, index: m.index });
  }
  return found.sort((a, b) => a.index - b.index).map((f) => f.name);
}

/** Per-subject counts: “10 histology and 5 anatomy questions”. */
function parseCatalogueCounts(text) {
  const counts = {};
  const t = String(text || "");
  for (const [pattern, name] of CATALOGUE_TYPOS) {
    const re = new RegExp(`\\b(\\d{1,3})\\s+(?:${pattern.source.replace(/^\\b\(|\)\\b$/g, "")})\\b`, "gi");
    let m;
    while ((m = re.exec(t))) counts[name] = (counts[name] || 0) + Number(m[1]);
  }
  return counts;
}

export function parseCatalogue(text) {
  const raw = String(text || "");
  for (const [pattern, name] of CATALOGUE_TYPOS) {
    if (pattern.test(raw)) return name;
  }
  return null;
}

export function inferCatalogueFromTopic(text) {
  const t = normalizeAskText(text).toLowerCase();
  if (!t.trim()) return null;
  const named = parseCatalogue(t);
  if (named) return named;
  if (/\b(embryo\w*|gastrulat\w*|fetal|foetal|placenta\w*|blastocyst|neurulation|somite\w*)\b/.test(t)) {
    return "embryology";
  }
  if (
    /\b(skeleton|skeletal|joints?|muscle system|gross anatomy|organ system|limbs?|thorax|abdomen|cranium|skull|brachial plexus|pelvis)\b/.test(
      t
    )
  ) {
    return "anatomy";
  }
  if (
    /\b(connective|tissues?|epithel\w*|cartilage|histolog\w*|fibroblasts?|adipose|collagen|basement membrane|glandular|myocard\w*|vessels?|blood|neurons?|nervous|glia\w*|stain\w*|microscop\w*)\b/.test(
      t
    )
  ) {
    return "histology";
  }
  return null;
}

const DIFFICULTY = /\b(easy|medium|hard|difficult|moderate|simple|advanced)\b/gi;

function canonicalDifficulty(word) {
  const w = String(word || "").toLowerCase();
  if (w === "difficult" || w === "advanced") return "hard";
  if (w === "moderate") return "medium";
  if (w === "simple") return "easy";
  return ["easy", "medium", "hard"].includes(w) ? w : "";
}

const TOPIC_STOP = /^(the|a|an|my|our|this|that|it|me|them|microlab|public|private|bank|slides?|questions?|quiz|quizzes|file|presentation)\b/i;

/** First topic phrase after “on / about / covering”, skipping catalogue names and sources. */
export function extractTopic(text) {
  const t = normalizeAskText(text);
  const re =
    /\b(?:on|about|covering|regarding|for)\s+([a-z][a-z' -]{2,60}?)(?=\s+(?:and|with|using|from|please|plz|for|in|out|by|make|create|give|that|which|\d)\b|[,.;:!?]|$)/gi;
  let m;
  while ((m = re.exec(t))) {
    const phrase = m[1].trim().replace(/\s+/g, " ");
    if (!phrase || TOPIC_STOP.test(phrase)) continue;
    if (parseCatalogue(phrase) && phrase.split(" ").length === 1) continue;
    if (/^(me|you|us|him|her|them|students?|my class)$/i.test(phrase)) continue;
    return phrase;
  }
  return null;
}

/**
 * Everything a quiz ask can carry:
 * count, per-difficulty mix, single difficulty, minutes, catalogue, bank, topic.
 */
export function parseQuizSpec(text) {
  const raw = normalizeAskText(text);
  const t = raw.toLowerCase();

  const mix = {};
  const mixRe = /\b(\d{1,3})\s*(easy|medium|hard|difficult|moderate|simple|advanced)\b/g;
  let mm;
  while ((mm = mixRe.exec(t))) {
    const level = canonicalDifficulty(mm[2]);
    if (level) mix[level] = (mix[level] || 0) + Number(mm[1]);
  }
  const mixTotal = Object.values(mix).reduce((a, b) => a + b, 0);

  const countMatch = t.match(
    /\b(\d{1,3})\s*(?:(?:easy|medium|hard|random|new|mcqs?|multiple[- ]choice|short|more)\s+)*(?:questions?|quiz(?:zes)?|qs|mcqs?|items)\b/
  );
  let count = countMatch ? Number(countMatch[1]) : null;
  // “20 quiz … 10 easy 5 medium 5 hard”: count is the total, mix splits it.
  // “4 easy and 6 hard questions”: the count regex only saw “6”, so use the sum.
  if (mixTotal && (!count || count < mixTotal)) count = mixTotal;

  const levels = [...new Set((t.match(DIFFICULTY) || []).map(canonicalDifficulty).filter(Boolean))];
  // One level only (“15 hard questions”) is a difficulty, not a mix.
  const singleLevel = Object.keys(mix).length === 1 && mixTotal === count;
  const difficulty = singleLevel ? Object.keys(mix)[0] : mixTotal ? "" : levels.length === 1 ? levels[0] : "";

  let minutes = null;
  const minMatch = t.match(/\b(\d{1,3})\s*[-–]?\s*(?:min|mins|minutes?)\b/);
  if (minMatch) minutes = Number(minMatch[1]);
  else if (/\bhalf (an )?hour\b/.test(t)) minutes = 30;
  else if (/\b(an|one|1)\s*hours?\b/.test(t)) minutes = 60;

  const privateBank = /\b(private|my (own )?(bank|questions|question bank)|own questions)\b/i.test(t);
  const microlabBank = /\b(microlab|public bank|public questions|public question bank)\b/i.test(t);

  const catalogueCounts = parseCatalogueCounts(t);
  const countedTotal = Object.values(catalogueCounts).reduce((a, b) => a + b, 0);
  // “10 histology and 10 anatomy questions” → 20 in total.
  if (countedTotal && (!count || (Object.keys(catalogueCounts).length > 1 && count < countedTotal))) {
    count = countedTotal;
  }

  return {
    count: count && count > 0 ? Math.min(100, count) : null,
    catalogues: parseCatalogues(t),
    catalogueCounts: Object.keys(catalogueCounts).length ? catalogueCounts : null,
    mix: mixTotal && !singleLevel ? mix : null,
    difficulty,
    minutes: minutes && minutes > 0 ? Math.min(300, minutes) : null,
    catalogue: parseCatalogue(t),
    inferredCatalogue: inferCatalogueFromTopic(t),
    bank: privateBank ? "private" : microlabBank ? "microlab" : null,
    topic: extractTopic(raw),
  };
}

function specHasDetail(spec) {
  return Boolean(
    spec &&
      (spec.count || spec.mix || spec.difficulty || spec.minutes || spec.catalogue || spec.bank || spec.topic)
  );
}

const RESULTS_WORDS =
  /\b(results?|scores?|marks?|grades?|performance|performed|averages?|analytics|analysis|progress|attempts?|pass(?:ed)? rate|failed|who failed|who passed|weak(?:est)? (?:topics?|students?)|struggl\w*)\b/i;

const PEOPLE_WORDS = /\b(students?|users?|professors?|lecturers?|teachers?|roster|directory|classmates|learners?)\b/i;

const PEOPLE_ASK =
  /\b(list|show|tell|who|details?|about|get|fetch|find|info|information|profile|emails?|contact|names?|how many|count|search|look up|lookup)\b/i;

/** Questions about the app itself (features, pages, buttons). */
const FEATURE_WORDS =
  /\b(stats|statistics page|dashboard|copilot|my library|library|community|course catalog|my titles|titles page|question bank|create quiz (page|form)|heat ?map|results page|presentations? (page|maker|list)|professors page|students page|sidebar|access code|qr code|image library|settings)\b/i;

const HOW_QUESTION =
  /^(?:\s*(?:hi|hello|hey|please|ok|okay)[,!\s]+)?(what is|what's|what are|how (?:do|can|to|does|should)|why (?:use|is|does|should)|where (?:is|do|can)|explain|help me (?:with|use)|guide me|tell me about)\b/i;

const APP_ACTION =
  /\bhow (?:do|can|should) i (?:create|make|use|add|invite|assign|share|open|upload|find|edit|export|import|publish)\b/i;

const LOOKUP_VERB =
  /\b(show|open|see|view|display|where|what(?:'s| is| was| are)?|which|list|find|details?|tell me about|look at|check)\b/i;

const LOOKUP_TARGET =
  /\b(that|the|this|my|last|latest|recent|previous|created|new|same|existing|current)\s+(?:\w+\s+)?(quiz|quizzes|draft)\b|\bmy quizzes\b|\b(access|quiz) code\b/i;

const REFERENCE =
  /\b(it|that|this|those|them|they|the same|same one|new one|another one|another|again|one more|the other)\b/i;

const CORRECTION =
  /^(no|nope|nah|not|wrong|i said|i meant|i mean|instead|actually|rather|use|only|just|but)\b/i;

const FILE_REFERENCE =
  /\b(this|that|the|my|uploaded|attached|same|your)\s+(files?|pdfs?|pptx?|powerpoints?|decks?|documents?|docs?|slides|lectures?|handouts?|uploads?|attachments?|notes|chapters?)\b|\b(slide|page|chapter|figure)\s*#?\s*\d+|\b(in|from) (it|there)\b/i;

const QUESTION_LIKE =
  /\?\s*$|^(what|how|why|who|when|where|which|can|could|does|do|is|are|explain|summari[sz]e|describe|list|compare|define|tell|give|translate|simplify|outline)\b/i;

/** Does this turn talk about the file the user uploaded earlier? */
function refersToFile(text, lastLane) {
  const t = String(text || "");
  if (FILE_REFERENCE.test(t)) return true;
  // Follow-ups to a file answer: “and the microglia?”, “explain more”, “why?”
  return (
    lastLane === LANES.FILE &&
    (REFERENCE.test(t) || QUESTION_LIKE.test(t.trim()) || t.trim().split(/\s+/).length <= 6)
  );
}

const CONTROL_PREFIX =
  /^(propose_presentation|confirm_apply_presentation|discard_presentation|presentation_[a-z0-9_]+[:\s]?|cat:|topic:|bank:|use_count:|start_quiz|generate_missing|confirm_save_generated|discard_generated|open_question_bank|open_copilot|continue_quiz|quiz_confirm|my_results|guide:)/i;

/**
 * Classify one turn.
 * ctx: { lastLane, hasQuizDraft, awaitingQuiz, hasPendingPresentation, awaitingTitle,
 *        hasSource (a file is attached to this message),
 *        fileInContext (a file was uploaded earlier in this conversation) }
 * Returns { lane, source: "explicit" | "context", deliverable, spec, usesFile }.
 */
export function classifyTurn(text, ctx = {}) {
  const raw = String(text || "").trim();
  const t = normalizeAskText(raw);
  const spec = parseQuizSpec(t);
  const deliverable = detectDeliverable(t);
  const create = hasCreateVerb(t);
  const lastLane = ctx.lastLane || null;
  const fileTurn = !!ctx.hasSource || (!!ctx.fileInContext && refersToFile(t, lastLane));
  const out = (lane, source = "explicit") => ({
    lane,
    source,
    deliverable,
    spec,
    // The answer should be built from the uploaded file.
    usesFile:
      fileTurn && [LANES.QUIZ, LANES.PRESENTATION, LANES.FILE, LANES.CLARIFY].includes(lane),
  });

  // A file sent with no words: ask what to do with it.
  if (!t) return out(ctx.hasSource ? LANES.FILE : LANES.CHAT);

  const control = t.match(CONTROL_PREFIX);
  if (control) {
    const key = control[1].toLowerCase();
    if (/presentation/.test(key)) return out(LANES.PRESENTATION);
    if (key === "my_results") return out(LANES.RESULTS);
    if (key === "guide:") return out(LANES.GUIDE);
    return out(LANES.QUIZ);
  }

  // “How do I create a quiz?” is a product question, not a create request.
  if (APP_ACTION.test(t)) return out(LANES.GUIDE);

  // The assistant just asked for the quiz name: a short reply is that name
  // (“Neuro block final”), unless it clearly asks for something else.
  if (
    ctx.awaitingTitle &&
    !ctx.hasSource &&
    deliverable !== "presentation" &&
    !QUESTION_LIKE.test(t) &&
    !RESULTS_WORDS.test(t) &&
    !(LOOKUP_VERB.test(t) && LOOKUP_TARGET.test(t)) &&
    !(PEOPLE_WORDS.test(t) && PEOPLE_ASK.test(t)) &&
    !(create && deliverable) &&
    t.split(/\s+/).length <= 12
  ) {
    return out(LANES.QUIZ, "context");
  }

  // About the uploaded file: build a quiz / presentation from it, or answer about it.
  // Only the user's own classes and quizzes (“list my students”) leave the file lane.
  if (fileTurn && !/\bmy (students?|class(es)?|quizzes|results|marks)\b/i.test(t)) {
    if (deliverable === "quiz") return out(LANES.QUIZ);
    if (deliverable === "presentation") return out(LANES.PRESENTATION);
    if (deliverable === "both") return out(LANES.CLARIFY);
    return out(LANES.FILE, ctx.hasSource || FILE_REFERENCE.test(t) ? "explicit" : "context");
  }

  const asksToMake = create && deliverable && deliverable !== "both";

  // Marks / results / roster — unless the user is clearly asking to build something.
  if (!asksToMake) {
    if (RESULTS_WORDS.test(t) && !/\bquiz (results page)\b/i.test(t)) {
      if (!HOW_QUESTION.test(t) || PEOPLE_WORDS.test(t) || /\bmy\b/i.test(t)) return out(LANES.RESULTS);
    }
    if (PEOPLE_WORDS.test(t) && PEOPLE_ASK.test(t) && !FEATURE_WORDS.test(t)) {
      return out(LANES.USERS);
    }
  }

  // “What is that quiz? Show it to me” — look up real data.
  if (!asksToMake && LOOKUP_VERB.test(t) && LOOKUP_TARGET.test(t) && !APP_ACTION.test(t)) {
    if (!(FEATURE_WORDS.test(t) && HOW_QUESTION.test(t))) return out(LANES.QUIZ_LOOKUP);
  }

  // Product how-to questions about a named feature.
  if (!asksToMake && (APP_ACTION.test(t) || (HOW_QUESTION.test(t) && FEATURE_WORDS.test(t)))) {
    return out(LANES.GUIDE);
  }

  if (deliverable === "both") return out(LANES.CLARIFY);
  if (deliverable === "presentation" && (create || /\b(about|on|for)\b/i.test(t))) {
    return out(LANES.PRESENTATION);
  }
  if (deliverable === "quiz") return out(LANES.QUIZ);

  // Follow-ups: “create a new one”, “no, use the microlab bank”, “show it”.
  const followUp =
    REFERENCE.test(t) || CORRECTION.test(t) || specHasDetail(spec) || t.split(/\s+/).length <= 4;
  if (lastLane && followUp && lastLane !== LANES.GUIDE && lastLane !== LANES.CHAT) {
    if (lastLane === LANES.QUIZ_LOOKUP && create) return out(LANES.QUIZ, "context");
    return out(lastLane, "context");
  }
  if ((ctx.awaitingQuiz || ctx.hasQuizDraft) && specHasDetail(spec)) {
    return out(LANES.QUIZ, "context");
  }
  if (ctx.hasPendingPresentation && /\bslides?\b/i.test(t)) {
    return out(LANES.PRESENTATION, "context");
  }

  // Quiz details without the word “quiz” (“10 questions 20 min”, “use the microlab bank”).
  if (spec.bank || (spec.count && (spec.minutes || spec.catalogue || spec.mix))) {
    return out(LANES.QUIZ);
  }

  // “Create a new one” with nothing to resolve “one” against.
  if (create && REFERENCE.test(t)) return out(LANES.CLARIFY);

  if (HOW_QUESTION.test(t) && FEATURE_WORDS.test(t)) return out(LANES.GUIDE);
  return out(LANES.CHAT);
}

/**
 * Lane of the most recent earlier user turn that was a task.
 * A product question in between (“how do I use the Question Bank?”) is a side
 * question, so “create a new one” afterwards still means the task before it.
 * history: [{ role, content }] oldest → newest (the current message excluded).
 */
export function lastLaneFromHistory(history) {
  const list = Array.isArray(history) ? history : [];
  let ctxLane = null;
  // Walk forward so each turn sees the lane before it (follow-ups chain).
  for (const row of list) {
    if (!row || row.role !== "user") continue;
    const turn = classifyTurn(row.content, { lastLane: ctxLane });
    if (![LANES.CHAT, LANES.CLARIFY, LANES.GUIDE].includes(turn.lane)) ctxLane = turn.lane;
  }
  return ctxLane;
}

const QUIZ_CHIP =
  /^(start_quiz|create-quiz|quiz_|cat:|topic:|bank:|use_count:|use_microlab|preset-|generate_missing|confirm_save_generated|discard_generated|open_question_bank|open_copilot|continue_quiz|change_difficulty|switch_bank|edit_|list_quizzes|histology-quiz|source_quiz|nav:quiz|go_titles|ask-quizzes)/i;
const PRESENTATION_CHIP =
  /^(propose_presentation|presentation_|source_presentation|retry_presentation|nav:presentation|guide:presentations|make-presentation)/i;
const RESULTS_CHIP = /^(open_results|open-results|my_results|refresh_results|retry-results|check-quiz|show_marks)/i;
const USERS_CHIP = /^(list_students|show_marks)/i;
const FILE_CHIP = /^source_summary/i;

/** Which task a suggestion chip starts: by id, else by what its words ask to make. */
export function chipLane(option) {
  const id = String(option?.id || "");
  if (PRESENTATION_CHIP.test(id)) return LANES.PRESENTATION;
  if (RESULTS_CHIP.test(id)) return LANES.RESULTS;
  if (USERS_CHIP.test(id)) return LANES.USERS;
  if (QUIZ_CHIP.test(id)) return LANES.QUIZ;
  if (FILE_CHIP.test(id)) return LANES.FILE;
  // Opening the page being discussed is not a new task.
  if (/^nav:/i.test(id)) return null;
  const words = `${option?.label || ""} ${String(option?.prompt || "").replace(/^__navigate__:.*/, "")}`;
  const deliverable = detectDeliverable(words);
  if (deliverable === "quiz") return LANES.QUIZ;
  if (deliverable === "presentation") return LANES.PRESENTATION;
  return null; // neutral: “Try again”, “Change title”, “Open Stats”, …
}

/** Tasks whose chips may follow a turn in each lane. */
const RELATED_LANES = {
  [LANES.QUIZ]: [LANES.QUIZ],
  [LANES.QUIZ_LOOKUP]: [LANES.QUIZ, LANES.QUIZ_LOOKUP],
  [LANES.PRESENTATION]: [LANES.PRESENTATION],
  // Student lists and their marks are one subject.
  [LANES.RESULTS]: [LANES.RESULTS, LANES.USERS],
  [LANES.USERS]: [LANES.USERS, LANES.RESULTS],
  [LANES.GUIDE]: [],
};

/**
 * Do what was asked, nothing else: drop chips that start a different task
 * (no “make a presentation” after “make a quiz with 20 questions”).
 * “Which one?” turns, attached-file choices and vague asks keep every chip.
 */
export function optionsForLane(options, lane) {
  const list = Array.isArray(options) ? options : [];
  const related = RELATED_LANES[lane];
  if (!related) return list;
  return list.filter((option) => {
    const chip = chipLane(option);
    return chip == null || related.includes(chip);
  });
}

/** Lanes worth remembering as “the task we're on” for follow-ups. */
export function isTaskLane(lane) {
  return !!lane && ![LANES.CHAT, LANES.CLARIFY, LANES.GUIDE].includes(lane);
}

/**
 * Previous task lane: the one stored on the draft after the last turn (it knows
 * about file turns, which the history text alone can't show), else from history.
 */
export function previousLane(draft, history) {
  return isTaskLane(draft?.last_lane) ? draft.last_lane : lastLaneFromHistory(history);
}

/**
 * Pick Microlab / private titles that match a free topic (“connective tissue”).
 * titles: [{ id, title, group }]. Returns the matching subset (possibly empty).
 */
export function matchTitlesForTopic(titles, topic) {
  const list = Array.isArray(titles) ? titles : [];
  const words = normalizeAskText(topic)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length >= 3 && !/^(and|the|for|with|from|about|quiz|questions?)$/.test(w))
    .map((w) => w.replace(/(ies)$/, "y").replace(/s$/, ""));
  if (!words.length) return [];
  const hay = (row) => `${row.group || ""} ${row.title || ""}`.toLowerCase();
  const all = list.filter((row) => words.every((w) => hay(row).includes(w)));
  if (all.length) return all;
  const GENERIC = /^(tissue|system|cell|structure|organ|part|type|general|basic)$/;
  const specific = words.filter((w) => !GENERIC.test(w));
  if (!specific.length || specific.length === words.length) return [];
  return list.filter((row) => specific.every((w) => hay(row).includes(w)));
}
